/*! Copyright (c) Safe As Milk. All rights reserved. */
class UtilityBar extends HTMLElement {
    connectedCallback() {
        this.id = this.getAttribute("id");
        this.carouselContainer = this.querySelector(".utility-announcement");
        this.carouselWrapper = this.querySelector(".utility-announcement__wrapper");
        this.carouselSlides = this.querySelectorAll(".utility-announcement__slides");
        if (this.carouselContainer != null) {
            this.interactions = true;
            this.isPausedByUser = false;
            this.autoplay = this.carouselContainer.hasAttribute("autoplay");
            this.autoplayDelay = Number(this.carouselContainer.getAttribute("autoplay-delay") || "5000");
            this.currentSlide = 0;
            this.autoplayInterval = null;
            // Prev and next buttons removed as requested
            this.#setupPlayPauseButton();
            this.#setupCloseButton();
            this.intersectionObserver = new IntersectionObserver(this.handleIntersection.bind(this));
            this.mutationObserver = new MutationObserver((() => {
                this.handleWindowResize();
            }));
            this.handleWindowResize();
            window.addEventListener("resize", (() => {
                this.handleWindowResize();
            }));
            this.observeMutations();
            if (this.carouselSlides.length > 0) {
                this.carouselSlides[this.currentSlide].dataset.active = true;
            }
            if (this.autoplay) {
                this.startAutoplay();
                this.carouselContainer.addEventListener("mouseenter", (() => this.stopAutoplay()));
                this.carouselContainer.addEventListener("mouseleave", (() => {
                    if (this.hasAttribute("popup-open") || this.isPausedByUser) return;
                    this.startAutoplay();
                }));
                this.carouselContainer.addEventListener("focusin", (() => this.stopAutoplay()));
                this.carouselContainer.addEventListener("focusout", ((e) => {
                    if (this.hasAttribute("popup-open") || this.isPausedByUser) return;
                    if (e.relatedTarget && !this.carouselContainer.contains(e.relatedTarget)) {
                        this.startAutoplay();
                    }
                }));
            }
            this.observeSlides();
            this.updateAccessibility();
        }
        this.setAttribute("loaded", "");
    }
    observeMutations() {
        this.mutationObserver.observe(this.carouselWrapper, {
            childList: true,
            subtree: true,
            characterData: true
        });
    }
    observeSlides() {
        this.carouselSlides.forEach((slide => {
            this.intersectionObserver.observe(slide);
        }));
    }
    handleWindowResize() {
        this.#calculateSlidesWidth();
    }
    handleScroll() {
        const slideWidth = this.carouselSlides[0].getBoundingClientRect().width;
        this.currentSlide = Math.round(this.carouselWrapper.scrollLeft / slideWidth);
        this.carouselSlides.forEach(((slide, index) => {
            if (index === this.currentSlide) {
                slide.setAttribute("data-active", "");
            } else {
                slide.removeAttribute("data-active");
            }
        }));
        this.updateAccessibility();
    }
    handleIntersection(entries) {
        entries.forEach((entry => {
            if (entry.isIntersecting) {
                this.carouselWrapper.addEventListener("scroll", (() => this.handleScroll()));
            } else {
                this.carouselWrapper.removeEventListener("scroll", (() => this.handleScroll()));
            }
        }));
    }
    disconnectedCallback() {
        if (!this.staticDisplay) {
            this.stopAutoplay();
            this.carouselPrevBtn?.removeEventListener("click", this.showPrevSlide);
            this.carouselNextBtn?.removeEventListener("click", this.showNextSlide);
            this.carouselContainer.removeEventListener("mouseenter", this.stopAutoplay);
            this.carouselContainer.removeEventListener("mouseleave", this.startAutoplay);
            this.intersectionObserver.disconnect();
            this.textSizeObserver.disconnect();
        }
        this.mutationObserver.disconnect();
        window.removeEventListener("resize", this.handleWindowResize);
        this.removeAttribute("loaded");
    }
    showPrevSlide() {
        if (this.currentSlide === 0) {
            this.moveToSlide(this.carouselSlides.length - 1);
        } else {
            this.moveToSlide(this.currentSlide - 1);
        }
        this.dispatchEvent(new CustomEvent("on:announcement-bar:slide-change", {
            detail: {
                currentSlide: this.currentSlide
            }
        }));
    }
    showNextSlide() {
        if (this.currentSlide === this.carouselSlides.length - 1) {
            this.moveToSlide(0);
        } else {
            this.moveToSlide(this.currentSlide + 1);
        }
        this.dispatchEvent(new CustomEvent("on:announcement-bar:slide-change", {
            detail: {
                currentSlide: this.currentSlide
            }
        }));
    }
    startAutoplay() {
        if (!this.interactions || this.isPausedByUser) return;
        if (this.autoplayInterval === null) {
            this.setAttribute("scrolling", "");
            this.autoplayInterval = setInterval((() => {
                this.showNextSlide();
            }), this.autoplayDelay);
        }
    }
    #setupPlayPauseButton() {
        this.playPauseBtn = this.querySelector(".utility-announcement__play-pause");
        if (!this.playPauseBtn) {
            this.playPauseBtn = document.createElement("button");
            this.playPauseBtn.type = "button";
            this.playPauseBtn.classList.add("utility-announcement__control-btn", "utility-announcement__play-pause");
            this.playPauseBtn.setAttribute("aria-label", "Pause announcement rotation");
            this.playPauseBtn.setAttribute("aria-pressed", "false");
            this.playPauseBtn.innerHTML = `
                <svg class="icon icon--pause" aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="6" y="4" width="4" height="16"></rect>
                  <rect x="14" y="4" width="4" height="16"></rect>
                </svg>
            `;
            this.carouselContainer.append(this.playPauseBtn);
        }
        if (this.playPauseBtn) {
            if (this.carouselSlides.length <= 1) {
                this.playPauseBtn.style.display = "none";
            } else {
                this.playPauseBtn.style.display = "";
            }
            this.playPauseBtn.addEventListener("click", () => this.togglePlayPause());
        }
    }
    #setupCloseButton() {
        this.closeBtn = this.querySelector(".utility-announcement__close");
        if (!this.closeBtn) {
            this.closeBtn = document.createElement("button");
            this.closeBtn.type = "button";
            this.closeBtn.classList.add("utility-announcement__control-btn", "utility-announcement__close");
            this.closeBtn.setAttribute("aria-label", "Close announcement bar");
            this.closeBtn.innerHTML = `
                <svg class="icon icon--close" aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            `;
            this.carouselContainer.append(this.closeBtn);
        }
        if (this.closeBtn) {
            this.closeBtn.addEventListener("click", () => this.dismissAnnouncementBar());
        }
    }
    togglePlayPause() {
        if (this.isPausedByUser) {
            this.isPausedByUser = false;
            this.startAutoplay();
            if (this.playPauseBtn) {
                this.playPauseBtn.setAttribute("aria-label", "Pause announcement rotation");
                this.playPauseBtn.setAttribute("aria-pressed", "false");
                this.playPauseBtn.innerHTML = `
                    <svg class="icon icon--pause" aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="6" y="4" width="4" height="16"></rect>
                      <rect x="14" y="4" width="4" height="16"></rect>
                    </svg>
                `;
            }
        } else {
            this.isPausedByUser = true;
            this.stopAutoplay();
            if (this.playPauseBtn) {
                this.playPauseBtn.setAttribute("aria-label", "Play announcement rotation");
                this.playPauseBtn.setAttribute("aria-pressed", "true");
                this.playPauseBtn.innerHTML = `
                    <svg class="icon icon--play" aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polygon points="5 3 19 12 5 21 5 3"></polygon>
                    </svg>
                `;
            }
        }
    }
    dismissAnnouncementBar() {
        this.stopAutoplay();
        this.style.display = 'none';
        this.setAttribute('hidden', 'hidden');
        const sectionEl = this.closest('.section--utility-bar');
        if (sectionEl) {
            sectionEl.style.display = 'none';
        }
    }
    stopAutoplay() {
        if (!this.interactions) return;
        if (this.autoplayInterval !== null) {
            this.removeAttribute("scrolling");
            clearInterval(this.autoplayInterval);
            this.autoplayInterval = null;
        }
    }
    moveToSlide(slideIndex, behavior = "smooth") {
        this.carouselWrapper.scrollTo({
            left: this.carouselSlides[slideIndex].offsetLeft,
            behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : behavior
        });
        this.currentSlide = slideIndex;
        this.carouselSlides.forEach(((slide, index) => {
            if (index === this.currentSlide) {
                slide.setAttribute("data-active", "");
            } else {
                slide.removeAttribute("data-active");
            }
        }));
        this.updateAccessibility();
    }
    stopInteractions() {
        this.interactions = false;
    }
    resumeInteractions() {
        this.interactions = true;
    }
    updateAccessibility() {
        this.carouselSlides.forEach(((slide, index) => {
            const isActive = (index === this.currentSlide);
            if (isActive) {
                slide.setAttribute("aria-hidden", "false");
                slide.querySelectorAll("a, button, [tabindex]").forEach((el) => {
                    el.removeAttribute("tabindex");
                });
            } else {
                slide.setAttribute("aria-hidden", "true");
                slide.querySelectorAll("a, button, [tabindex]").forEach((el) => {
                    el.setAttribute("tabindex", "-1");
                });
            }
        }));
    }
    #calculateSlidesWidth() {
        this.slidesWidth = 0;
        this.containerWidth = window.innerWidth * .89;
        const {carouselSlides: carouselSlides} = this;
        carouselSlides.forEach((slide => {
            const announcementMessage = slide.querySelector(".utility-announcement__message");
            const announcementText = slide.querySelector(".utility-announcement__text");
            const computedStyles = window.getComputedStyle(announcementMessage);
            const textWidth = announcementText.offsetWidth + parseFloat(computedStyles.paddingLeft) + parseFloat(computedStyles.paddingRight);
            this.slidesWidth += textWidth;
        }));
        this.slidesWrap = this.slidesWidth > this.containerWidth;
    }
    #createNavigation() {
        this.carouselPrevBtn = document.createElement("button");
        this.carouselPrevBtn.classList.add("utility-announcement__carousel-button", "carousel-prev");
        this.carouselPrevBtn.setAttribute("aria-label", "Previous Announcement");
        const arrowPreviousSVG = document.getElementById("template-icon-chevron").content.cloneNode(true).firstElementChild;
        arrowPreviousSVG.classList.add("icon--left");
        this.carouselPrevBtn.append(arrowPreviousSVG);
        this.carouselContainer.prepend(this.carouselPrevBtn);
        this.carouselNextBtn = document.createElement("button");
        this.carouselNextBtn.classList.add("utility-announcement__carousel-button", "carousel-next");
        this.carouselNextBtn.setAttribute("aria-label", "Next Announcement");
        const arrowNextSVG = document.getElementById("template-icon-chevron").content.cloneNode(true).firstElementChild;
        this.carouselNextBtn.append(arrowNextSVG);
        this.carouselContainer.append(this.carouselNextBtn);
        this.carouselPrevBtn.addEventListener("click", (() => this.showPrevSlide()));
        this.carouselNextBtn.addEventListener("click", (() => this.showNextSlide()));
    }
}

customElements.define("utility-bar", UtilityBar);
//# sourceMappingURL=utility-bar.js.map
